import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Avatar,
  Stack,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  FormHelperText,
  Autocomplete,
  Divider,
  Card,
  Tooltip,
  Popover,
  FormControlLabel,
  Switch,
  CircularProgress,
} from "@mui/material";
import {
  LocalizationProvider,
  StaticDatePicker,
  DatePicker,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import {
  DashboardCard,
  PageContainer,
  Breadcrumb,
  PatientModal,
} from "../common";
import { client } from "src/common/axiosClient";
import moment, { Moment } from "moment";
import { getAge } from "src/utils/getAge";
import TodoCount from "../dashboards/TodoCount";
import ProgressCompanyCount from "../dashboards/ProgressCompanyCount";
import icon1Img from "src/assets/images/svgs/icon-paypal.svg";
import ShareCompanyCount from "../dashboards/ShareCompanyCount";
import { Add } from "@mui/icons-material";
import {
  IconArrowUpLeft,
  IconCheck,
  IconCalendar,
  IconPlus,
  IconUserPlus,
  IconAlarm,
  IconNote,
  IconDeviceFloppy,
  IconX,
  IconArrowLeft,
  IconArrowRight,
} from "@tabler/icons";
import MonthCompany from "../dashboards/MonthCompany";
import ChatWeekly from "./ChatWeekly";
import { SelectChangeEvent } from "@mui/material/Select";

const MainTwo = () => {
  const [loading, setLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(moment());
  const [doctorList, setDoctorList] = useState<any[]>([]);
  const [reservation, setReservation] = useState<any[]>([]);
  const [currentTime, setCurrentTime] = useState<string>("");
  const [patient, setPatient] = useState<any>(null);
  const [doctorSchedules, setDoctorSchedules] = useState<Record<number, any>>(
    {}
  );
  const [dayOffData, setDayOffData] = useState<Record<number, any[]>>({});

  const currentRowRef = useRef<HTMLTableRowElement | null>(null);
  const [selectedDoctor, setSelectedDoctor] = useState<any>(null);
  const [selectedTime, setSelectedTime] = useState<Moment | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<any>(null);
  const [reservationMemo, setReservationMemo] = useState<string>("");
  const [patients, setPatients] = useState<any[]>([]);
  const [openReservationModal, setOpenReservationModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openPatientModal, setOpenPatientModal] = useState<boolean>(false);
  const [patientData, setPatientData] = useState({
    name: "",
    birthDate: moment().subtract(20, "years").format("YYYY-MM-DD"),
    gender: "MALE",
    mobile: "",
  });
  const [searchQuery, setSearchQuery] = useState("");

  // 페이지네이션과 검색을 위한 상태 추가
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchTimeout, setSearchTimeout] = useState<NodeJS.Timeout | null>(
    null
  );

  // 시간 설정 - 8시부터 24시까지, 30분 단위
  const startHour = 8; // 시작 시간 (고정: 8시)
  const endHour = 24; // 종료 시간 (고정: 24시)
  const useHalfHourSlots = true; // 30분 단위 표시 (항상 true)

  // 시간 배열 생성 함수 (30분 단위로 생성)
  const generateTimeArray = () => {
    const result = [];
    for (let hour = startHour; hour <= endHour; hour++) {
      const hourStr = hour < 10 ? `0${hour}` : `${hour}`;
      result.push(`${hourStr}:00`);

      // 30분 단위 추가 (마지막 시간에는 30분 단위 추가하지 않음)
      if (hour < endHour) {
        result.push(`${hourStr}:30`);
      }
    }
    return result;
  };

  // 동적으로 시간 배열 생성
  const timeArray = generateTimeArray();

  const handleDateChange = (newDate: Moment) => {
    setSelectedDate(newDate);
    getDoctorReservations(newDate);
  };

  const handlePrevDay = () => {
    const prevDay = moment(selectedDate).subtract(1, "day");
    setSelectedDate(prevDay);
    getDoctorReservations(prevDay);
  };

  const handleNextDay = () => {
    const nextDay = moment(selectedDate).add(1, "day");
    setSelectedDate(nextDay);
    getDoctorReservations(nextDay);
  };

  useEffect(() => {
    getDoctorReservations(selectedDate);
  }, [selectedDate]);

  useEffect(() => {
    getDoctorList();
    getDoctorReservations(selectedDate);

    // 현재 시간을 30분 단위로 업데이트하는 함수
    const updateCurrentTime = () => {
      const now = moment();
      const minutes = now.minutes();
      // 30분 단위로 시간 설정 (0~29분이면 정시, 30~59분이면 30분)
      const formattedTime = `${now.format("HH")}:${minutes < 30 ? "00" : "30"}`;
      setCurrentTime(formattedTime);
    };

    updateCurrentTime(); // 초기 실행
    const interval = setInterval(updateCurrentTime, 60000); // 1분마다 업데이트
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const reservationInterval = setInterval(() => {
      getDoctorReservations(selectedDate);
    }, 1000); // Update every 10 seconds

    return () => clearInterval(reservationInterval);
  }, []);

  useEffect(() => {
    if (currentRowRef.current) {
      currentRowRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [currentTime]);

  useEffect(() => {
    // 의사가 있을 때 각 의사의 연차 정보를 가져옵니다
    if (doctorList.length > 0) {
      doctorList.forEach((doctor) => {
        getDoctorDayOff(doctor.id);
      });
    }
  }, [doctorList, selectedDate]);

  const getDoctorReservations = async (selectedDate: Moment) => {
    const today = new Date(selectedDate.toString());

    const from = new Date(today.setDate(today.getDate()))
      .toISOString()
      .split("T")[0];
    const to = new Date(today.setDate(today.getDate()))
      .toISOString()
      .split("T")[0];

    const response = await client.get(
      `admin/api/v1/reservation/MANUAL_THERAPIST/period?from=${from}&to=${from}`
    );

    if (response.status === 200) {
      setReservation([...response.data.content]);
    } else {
      console.log(response);
    }
  };

  const getDoctorList = async () => {
    setDoctorList([]);
    const response = await client.get(
      "admin/api/v1/medical-staff/MANUAL_THERAPIST"
    );

    if (response.status === 200) {
      const doctors = response.data.content || [];
      setDoctorList(doctors);

      // 의사 목록을 가져온 후 각 의사의 스케줄 정보를 가져옵니다
      for (const doctor of doctors) {
        await getDoctorSchedule(doctor.id);
      }
    } else {
      console.log(response);
    }
  };

  // 의사의 스케줄 정보를 가져오는 함수
  const getDoctorSchedule = async (doctorId: number) => {
    try {
      const response = await client.get(
        `admin/api/v1/schedule-meta/MANUAL_THERAPIST/${doctorId}`
      );
      if (response.status === 200 && response.data.content) {
        setDoctorSchedules((prev) => ({
          ...prev,
          [doctorId]: response.data.content,
        }));
      }
    } catch (error) {
      console.error(
        `의사 ID ${doctorId}의 스케줄을 가져오는데 실패했습니다:`,
        error
      );
    }
  };

  // 의사의 연차 정보를 가져오는 함수
  const getDoctorDayOff = async (doctorId: number) => {
    try {
      const firstDay = moment(selectedDate)
        .startOf("month")
        .format("YYYY-MM-DD");
      const lastDay = moment(selectedDate).endOf("month").format("YYYY-MM-DD");

      const response = await client.get(
        `admin/api/v1/schedule-day-off/MANUAL_THERAPIST/${doctorId}/period?from=${firstDay}&to=${lastDay}`
      );

      if (response.status === 200) {
        setDayOffData((prev) => ({
          ...prev,
          [doctorId]: response.data.content || [],
        }));
      }
    } catch (error) {
      console.error(
        `의사 ID ${doctorId}의 연차 정보를 가져오는데 실패했습니다:`,
        error
      );
    }
  };

  // 의사의 점심 시간 정보를 가져오는 함수
  const getDoctorLunchTime = (doctorId: number, date: Moment) => {
    const schedule = doctorSchedules[doctorId];
    if (!schedule) return { startAt: "13:00:00", endAt: "14:00:00" }; // 기본값

    // 영어 요일명을 API의 요일명 필드로 변환
    const dayMap: Record<string, string> = {
      Monday: "monday",
      Tuesday: "tuesday",
      Wednesday: "wednesday",
      Thursday: "thursday",
      Friday: "friday",
      Saturday: "saturday",
      Sunday: "sunday",
    };

    const dayOfWeek = dayMap[date.format("dddd")] || "monday";
    const daySchedule = schedule[dayOfWeek];

    if (!daySchedule || !daySchedule.lunch)
      return { startAt: "13:00:00", endAt: "14:00:00" };

    return daySchedule.lunch;
  };

  // 해당 시간이 점심 시간인지 확인하는 함수
  const isLunchTime = (doctorId: number, timeStr: string, date: Moment) => {
    const lunchTime = getDoctorLunchTime(doctorId, date);

    if (!lunchTime) return false;

    const time = moment(timeStr, "HH:mm");
    const startTime = moment(lunchTime.startAt, "HH:mm:ss");
    const endTime = moment(lunchTime.endAt, "HH:mm:ss");

    return time.isBetween(startTime, endTime, undefined, "[)");
  };

  // 점심 시간 텍스트 가져오기
  const getLunchTimeText = (doctorId: number, date: Moment) => {
    const lunchTime = getDoctorLunchTime(doctorId, date);
    if (!lunchTime) return "점심시간";

    const startTime = moment(lunchTime.startAt, "HH:mm:ss").format("HH:mm");
    const endTime = moment(lunchTime.endAt, "HH:mm:ss").format("HH:mm");

    return `점심시간 (${startTime}-${endTime})`;
  };

  // 해당 의사가 해당 시간에 근무 중인지 확인하는 함수
  const isDoctorAvailable = (
    doctorId: number,
    timeStr: string,
    date: Moment
  ) => {
    const schedule = doctorSchedules[doctorId];
    if (!schedule) return true; // 스케줄 정보가 없으면 기본적으로 가능하다고 표시

    // 영어 요일명을 API의 요일명 필드로 변환
    const dayMap: Record<string, string> = {
      Monday: "monday",
      Tuesday: "tuesday",
      Wednesday: "wednesday",
      Thursday: "thursday",
      Friday: "friday",
      Saturday: "saturday",
      Sunday: "sunday",
    };

    const dayOfWeek = dayMap[date.format("dddd")] || "monday";
    const daySchedule = schedule[dayOfWeek];
    if (!daySchedule) return true;

    const time = moment(timeStr, "HH:mm");

    // 시간대 확인 (오전, 점심, 오후, 야간)
    const periods = [
      { name: "morning", displayName: "오전" },
      { name: "lunch", displayName: "점심" },
      { name: "afternoon", displayName: "오후" },
      { name: "night", displayName: "야간" },
    ];

    // 각 시간대 확인
    for (const period of periods) {
      const periodSchedule = daySchedule[period.name];
      if (!periodSchedule) continue;

      const startTime = moment(periodSchedule.startAt, "HH:mm:ss");
      const endTime = moment(periodSchedule.endAt, "HH:mm:ss");

      // 점심 시간이면 휴진
      if (period.name === "lunch") {
        if (time.isBetween(startTime, endTime, undefined, "[)")) {
          return false;
        }
      }
      // 그 외 시간대는 근무 시간
      else if (time.isBetween(startTime, endTime, undefined, "[)")) {
        return true;
      }
    }

    // 모든 시간대를 확인했는데 해당되는 시간이 없으면 휴진
    return false;
  };

  const reservationDelete = async (id: number) => {
    const response = await client.delete(
      `admin/api/v1/reservation/MANUAL_THERAPIST/${id}`
    );
    if (response.status === 200) {
      console.log(response);
      getDoctorReservations(selectedDate);
    } else {
      console.log(response);
    }
  };

  const reservationVisit = async (id: number) => {
    const response = await client.put(
      `admin/api/v1/reservation/MANUAL_THERAPIST/${id}/visit`
    );
    if (response.status === 200) {
      console.log(response);
      setPatient((prevPatient: any) => ({
        ...prevPatient,
        visitedAt: moment().toISOString(),
      }));

      getDoctorReservations(selectedDate);
    } else {
      console.log(response);
    }
  };

  const getReservationForTime = (doctorId: number, time: string) => {
    const reservationForTime = reservation.filter(
      (res) =>
        res.medicalStaff.id === doctorId &&
        moment(res.reservedAt).format("HH:mm") === time &&
        res.status !== "DELETED"
    );

    if (reservationForTime.length > 0) {
      return (
        <Stack
          spacing={0.5}
          width="100%"
          sx={{
            p: 0.5,
            borderRadius: "3px",
            bgcolor: "rgba(255, 255, 255, 0.8)",
          }}
        >
          {reservationForTime.map((res) => (
            <Box
              key={res.id}
              sx={{
                display: "flex",
                alignItems: "center",
                p: 0.3,
                borderRadius: "3px",
                transition: "all 0.2s",
                cursor: "pointer",
                border: "1px solid rgba(0, 0, 0, 0.05)",
                boxShadow: "0 1px 1px rgba(0,0,0,0.02)",
                backgroundColor: res.visitedAt
                  ? "rgba(76, 175, 80, 0.06)"
                  : "rgba(25, 118, 210, 0.04)",
                "&:hover": {
                  backgroundColor: res.visitedAt
                    ? "rgba(76, 175, 80, 0.12)"
                    : "rgba(25, 118, 210, 0.08)",
                },
                height: "18px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                setPatient(res);
              }}
            >
              {/* 초진/재진 + 환자명 + 나이 + 성별 한 줄에 표시 */}
              <Typography
                variant="caption"
                sx={{
                  fontSize: "10px",
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    bgcolor:
                      res.visitStatus === "FOLLOW_UP"
                        ? "rgba(76, 175, 80, 0.12)"
                        : "rgba(255, 152, 0, 0.12)",
                    color:
                      res.visitStatus === "FOLLOW_UP" ? "#2e7d32" : "#e65100",
                    borderRadius: "2px",
                    px: 0.3,
                    mr: 0.5,
                    fontSize: "9px",
                    fontWeight: "bold",
                  }}
                >
                  {res.visitStatus === "FOLLOW_UP" ? "재" : "초"}
                </Box>

                <Box component="span" sx={{ fontWeight: 600, mr: 0.3 }}>
                  {res.patient?.name || "-"}
                </Box>

                <Box
                  component="span"
                  sx={{ color: "text.secondary", fontSize: "9px", mr: 0.5 }}
                >
                  {res.patient?.birthDate
                    ? `${getAge(res.patient.birthDate)}`
                    : ""}
                </Box>

                <Box
                  component="span"
                  sx={{
                    fontSize: "9px",
                    fontWeight: "bold",
                    color:
                      res.patient?.gender === "MALE" ? "#1976d2" : "#d32f2f",
                    ml: "auto",
                  }}
                >
                  {res.patient?.gender === "MALE" ? "남" : "여"}
                </Box>

                {res.visitedAt && (
                  <Box
                    component="span"
                    title={`방문 완료: ${moment(res.visitedAt).format(
                      "HH:mm"
                    )}`}
                  >
                    <IconCheck
                      size="10"
                      color="#4CAF50"
                      style={{ marginLeft: "2px" }}
                    />
                  </Box>
                )}
              </Typography>
            </Box>
          ))}
          <Box
            sx={{
              display: "flex",
              width: "100%",
              mt: 0.5,
            }}
          >
            {(() => {
              // 의사의 스케줄 정보를 가져옴
              const schedule = doctorSchedules[doctorId];

              // 최대 예약 가능 환자 수 (API에서 받아온 값)
              const maxPatients = schedule?.availablePatientCount || 1;

              // 해당 시간에 이미 예약된 환자 수 확인
              const timeSlotReservations = reservation.filter(
                (res) =>
                  res.medicalStaff.id === doctorId &&
                  moment(res.reservedAt).format("HH:mm") === time &&
                  res.status !== "DELETED"
              );

              // 현재 예약 가능한 남은 환자 수
              const availableCount = Math.max(
                0,
                maxPatients - timeSlotReservations.length
              );

              // 예약 가능한 환자 수가 있는 경우에만 예약 버튼 표시
              return availableCount > 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(93, 135, 255, 0.1)",
                    borderRadius: "4px",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const timeText = time;
                    const [hours, minutes] = timeText.split(":").map(Number);
                    const selectedDateTime = moment(selectedDate)
                      .hours(hours)
                      .minutes(minutes);
                    handleOpenReservationModal(
                      doctorList.find((doc) => doc.id === doctorId),
                      selectedDateTime
                    );
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: "primary.main",
                      fontWeight: "medium",
                      bgcolor: "primary.light",
                      px: 1,
                      py: 0.3,
                      borderRadius: "4px",
                      fontSize: "9px",
                    }}
                  >
                    + 예약하기
                  </Typography>
                </Box>
              ) : null;
            })()}
          </Box>
        </Stack>
      );
    }

    return "";
  };

  const handleOpenReservationModal = (
    doctor?: any,
    selectedDateTime?: Moment
  ) => {
    if (doctor) {
      setSelectedDoctor(doctor);
    } else {
      setSelectedDoctor(doctorList.length > 0 ? doctorList[0] : null);
    }

    if (selectedDateTime) {
      setSelectedTime(selectedDateTime);
    } else {
      setSelectedTime(moment().hours(9).minutes(0).seconds(0));
    }

    // 환자와 기타 입력 필드 초기화
    setSelectedPatient(null);
    setReservationMemo("");
    setSearchQuery("");
    setCurrentPage(1);
    setHasMore(true);

    // API에서 환자 목록 가져오기
    getPatientList(1, "");

    setOpenReservationModal(true);
  };

  const handleCloseReservationModal = () => {
    setOpenReservationModal(false);
  };

  const handleReservationSubmit = async () => {
    if (!selectedDoctor || !selectedTime || !selectedPatient) {
      alert("의사, 환자, 예약 시간을 모두 선택해주세요.");
      return;
    }

    setSubmitting(true);
    try {
      // 초진/재진 자동 설정 (lastVisitAt이 null이면 초진, 아니면 재진)
      const visitStatus = selectedPatient.lastVisitedAt ? "FOLLOW_UP" : "FIRST";

      // 예약 데이터 준비
      const reservationData = {
        patientId: selectedPatient.id,
        medicalStaffType: "DOCTOR",
        medicalStaffId: selectedDoctor.id,
        reservedAt: selectedTime.format("YYYY-MM-DDTHH:mm:ss"),
        memo: reservationMemo,
        visitStatus: visitStatus,
      };

      // API 호출
      const response = await client.post(
        "admin/api/v1/reservation/MANUAL_THERAPIST",
        reservationData
      );

      if (response.status === 200 || response.status === 201) {
        alert("예약이 성공적으로 등록되었습니다.");
        handleCloseReservationModal();
        getDoctorReservations(selectedDate);
      } else {
        alert(
          `예약 등록에 실패했습니다: ${
            response.data?.message || "알 수 없는 오류가 발생했습니다."
          }`
        );
        console.error("예약 등록 실패:", response);
      }
    } catch (error: any) {
      alert(
        `예약 등록 중 오류가 발생했습니다: ${
          error.response?.data?.message || error.message || "알 수 없는 오류"
        }`
      );
      console.error("예약 등록 오류:", error);
    } finally {
      setSubmitting(false);
    }
  };

  // 환자 목록을 가져오는 함수 (페이지네이션 추가)
  const getPatientList = async (page: number = 1, nameQuery: string = "") => {
    try {
      setIsLoading(true);
      const perPage = 20; // 한 번에 가져올 환자 수
      let url = `admin/api/v1/patient/all?page=${page}&perPage=${perPage}`;

      // 이름 검색이 있는 경우 쿼리 추가
      if (nameQuery) {
        url += `&name=${encodeURIComponent(nameQuery)}`;
      }

      const response = await client.get(url);

      if (response.status === 200) {
        const newPatients = response.data.content || [];

        // 첫 페이지이면 목록 교체, 아니면 추가
        if (page === 1) {
          setPatients(newPatients);
        } else {
          setPatients((prev) => [...prev, ...newPatients]);
        }

        // 더 불러올 데이터가 있는지 확인
        setHasMore(newPatients.length === perPage);
        setCurrentPage(page);
      } else {
        console.error("환자 목록을 가져오는데 실패했습니다:", response);
      }
    } catch (error) {
      console.error("환자 목록 조회 중 오류 발생:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // 인접한 휴진 시간인지 확인하는 함수
  const checkAdjacentUnavailableTimes = (
    doctorId: number,
    time: string,
    date: Moment
  ) => {
    // 현재 시간을 Moment로 변환
    const currentTime = moment(time, "HH:mm");

    // 이전 시간
    const prevTime = moment(currentTime).subtract(1, "hour").format("HH:00");
    // 다음 시간
    const nextTime = moment(currentTime).add(1, "hour").format("HH:00");

    // 이전 시간이 휴진인지 확인
    const isPrevUnavailable = !isDoctorAvailable(doctorId, prevTime, date);
    // 다음 시간이 휴진인지 확인
    const isNextUnavailable = !isDoctorAvailable(doctorId, nextTime, date);

    // 시작, 중간, 끝 유형 반환
    if (isPrevUnavailable && isNextUnavailable) return "middle"; // 앞뒤 모두 휴진
    if (isPrevUnavailable) return "end"; // 앞만 휴진
    if (isNextUnavailable) return "start"; // 뒤만 휴진
    return "isolated"; // 독립된 휴진
  };

  const handleCalendarClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    console.log("Calendar button clicked");
    setAnchorEl(event.currentTarget);
  };

  const handleCalendarClose = () => {
    console.log("Calendar closing");
    setAnchorEl(null);
  };

  const handleCalendarDateSelect = (date: Moment) => {
    setSelectedDate(date);
    getDoctorReservations(date);
    handleCalendarClose();
  };

  // 요일 변환 함수
  const getKoreanDayOfWeek = (date: Moment) => {
    const days = ["일", "월", "화", "수", "목", "금", "토"];
    return days[date.day()];
  };

  // 특정 시간에 연차인지 확인하는 함수 (시간대에 따라 다르게 처리)
  const isTimeOff = (doctorId: number, date: Moment, time: string) => {
    const dayOffArray = dayOffData[doctorId] || [];
    const dayOffDate = date.format("YYYY-MM-DD");

    return dayOffArray.some((dayOff) => {
      const offDate = moment(dayOff.date).format("YYYY-MM-DD");
      if (offDate !== dayOffDate) return false;

      // 시간대에 따라 다르게 처리
      const hour = parseInt(time.split(":")[0]);

      if (dayOff.type === "FULL") {
        // 전체 시간 휴가
        return true;
      } else if (dayOff.type === "MORNING" && hour >= 8 && hour <= 12) {
        // 오전 휴가 (8-12시)
        return true;
      } else if (dayOff.type === "AFTERNOON" && hour >= 14 && hour <= 17) {
        // 오후 휴가 (14-17시)
        return true;
      }

      return false;
    });
  };

  // 연차의 메모 가져오기
  const getTimeOffMemo = (doctorId: number, date: Moment, time: string) => {
    const dayOffArray = dayOffData[doctorId] || [];
    const dayOffDate = date.format("YYYY-MM-DD");

    const matchedDayOff = dayOffArray.find((dayOff) => {
      const offDate = moment(dayOff.date).format("YYYY-MM-DD");
      if (offDate !== dayOffDate) return false;

      // 시간대에 따라 다르게 처리
      const hour = parseInt(time.split(":")[0]);

      if (dayOff.type === "FULL") {
        // 전체 시간 휴가
        return true;
      } else if (dayOff.type === "MORNING" && hour >= 8 && hour <= 12) {
        // 오전 휴가 (8-12시)
        return true;
      } else if (dayOff.type === "AFTERNOON" && hour >= 14 && hour <= 17) {
        // 오후 휴가 (14-17시)
        return true;
      }

      return false;
    });

    if (matchedDayOff) {
      if (matchedDayOff.type === "FULL") return "종일 연차";
      if (matchedDayOff.type === "MORNING") return "오전 연차";
      if (matchedDayOff.type === "AFTERNOON") return "오후 연차";
      return matchedDayOff.reason || "연차";
    }

    return "연차";
  };

  // 예약 가능 시간 슬롯 여부 확인 함수
  const isTimeSlotAvailable = (
    doctorId: number,
    timeStr: string,
    date: Moment
  ) => {
    // 기본적으로 의사가 근무중이고, 점심시간이나 연차가 아닌지 확인
    if (!isDoctorAvailable(doctorId, timeStr, date)) return false;
    if (isLunchTime(doctorId, timeStr, date)) return false;
    if (isTimeOff(doctorId, date, timeStr)) return false;

    const schedule = doctorSchedules[doctorId];
    if (!schedule) return true; // 스케줄 정보가 없으면 기본적으로 가능하다고 표시

    // 해당 시간에 이미 예약된 환자 수 확인
    const timeSlotReservations = reservation.filter(
      (res) =>
        res.medicalStaff.id === doctorId &&
        moment(res.reservedAt).format("HH:mm") === timeStr &&
        res.status !== "DELETED"
    );

    // intervalMin 및 availablePatientCount 가져오기
    const availablePatientCount = schedule.availablePatientCount || 1; // 기본값 1명

    // 해당 시간에 최대 예약 가능 환자 수와 현재 예약된 환자 수 비교
    return timeSlotReservations.length < availablePatientCount;
  };

  // 예약 가능한 환자 수 계산 함수
  const getAvailablePatientCount = (
    doctorId: number,
    timeStr: string,
    date: Moment
  ) => {
    if (
      !isDoctorAvailable(doctorId, timeStr, date) ||
      isLunchTime(doctorId, timeStr, date) ||
      isTimeOff(doctorId, date, timeStr)
    )
      return 0;

    const schedule = doctorSchedules[doctorId];
    if (!schedule) return 1; // 스케줄 정보가 없으면 기본값 1 반환

    // 해당 시간에 이미 예약된 환자 수 확인
    const timeSlotReservations = reservation.filter(
      (res) =>
        res.medicalStaff.id === doctorId &&
        moment(res.reservedAt).format("HH:mm") === timeStr &&
        res.status !== "DELETED"
    );

    // 예약 가능한 환자 수 계산
    const availablePatientCount = schedule.availablePatientCount || 1;
    return Math.max(0, availablePatientCount - timeSlotReservations.length);
  };

  // 진료 간격 정보 가져오기
  const getDoctorIntervalMin = (doctorId: number) => {
    const schedule = doctorSchedules[doctorId];
    if (!schedule) return 15; // 기본값 15분
    return schedule.intervalMin || 15;
  };

  // 환자 등록 모달 관련 함수들
  const handleOpenPatientModal = () => {
    // 이름이 검색창에 입력되어 있다면 그 이름으로 기본값 설정
    if (searchQuery) {
      setPatientData({
        ...patientData,
        name: searchQuery,
      });
    }
    setOpenPatientModal(true);
  };

  const handleClosePatientModal = () => {
    setOpenPatientModal(false);
    // 모달 닫을 때 입력 필드 초기화
    setPatientData({
      name: "",
      birthDate: moment().subtract(20, "years").format("YYYY-MM-DD"),
      gender: "MALE",
      mobile: "",
    });
  };

  const handlePatientInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    // 휴대폰 번호 자동 하이픈 삽입
    if (name === "mobile") {
      // 숫자만 추출
      const numbersOnly = value.replace(/[^0-9]/g, "");

      // 적절한 위치에 하이픈 삽입
      let formattedNumber = "";
      if (numbersOnly.length <= 3) {
        formattedNumber = numbersOnly;
      } else if (numbersOnly.length <= 7) {
        formattedNumber = `${numbersOnly.slice(0, 3)}-${numbersOnly.slice(3)}`;
      } else {
        formattedNumber = `${numbersOnly.slice(0, 3)}-${numbersOnly.slice(
          3,
          7
        )}-${numbersOnly.slice(7, 11)}`;
      }

      setPatientData({
        ...patientData,
        [name]: formattedNumber,
      });
    } else {
      setPatientData({
        ...patientData,
        [name]: value,
      });
    }
  };

  const handlePatientSelectChange = (event: SelectChangeEvent) => {
    const { name, value } = event.target;
    setPatientData({
      ...patientData,
      [name]: value,
    });
  };

  const handleQuickBirthDate = (years: number) => {
    setPatientData({
      ...patientData,
      birthDate: moment().subtract(years, "years").format("YYYY-MM-DD"),
    });
  };

  const handlePatientSubmit = async () => {
    if (
      !patientData.name ||
      !patientData.birthDate ||
      !patientData.gender ||
      !patientData.mobile
    ) {
      alert("모든 필드를 입력해주세요.");
      return;
    }

    // 휴대폰 번호 형식 검증
    const mobileRegex = /^[0-9]{3}-[0-9]{4}-[0-9]{4}$/;
    if (!mobileRegex.test(patientData.mobile)) {
      alert("휴대폰 번호는 000-0000-0000 형식으로 입력해주세요.");
      return;
    }

    try {
      setSubmitting(true);
      const response = await client.post("admin/api/v1/patient", {
        name: patientData.name,
        birthDate: patientData.birthDate,
        gender: patientData.gender,
        mobile: patientData.mobile,
      });

      if (response.status === 201) {
        alert("환자 등록이 완료되었습니다.");
        handleClosePatientModal();

        // 환자 목록 새로고침 및 새로 등록된 환자 선택
        await getPatientList();
      }
    } catch (error) {
      console.error("환자 등록 중 오류가 발생했습니다: ", error);
      alert("환자 등록에 실패했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  // 스크롤 이벤트 핸들러 (인피니트 스크롤용)
  const handlePatientListScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;

    // 스크롤이 하단에 도달했고, 더 로드할 데이터가 있으며, 로딩 중이 아닐 때
    if (
      scrollHeight - scrollTop <= clientHeight + 100 &&
      hasMore &&
      !isLoading
    ) {
      getPatientList(currentPage + 1, searchQuery);
    }
  };

  // 검색어 변경 핸들러
  const handleSearchQueryChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    newValue: string
  ) => {
    setSearchQuery(newValue);

    // 타이핑이 멈추면 검색 실행 (디바운싱)
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    const timeout = setTimeout(() => {
      setCurrentPage(1);
      getPatientList(1, newValue);
    }, 500); // 500ms 후 검색 실행

    setSearchTimeout(timeout);
  };

  return (
    <PageContainer>
      <Box sx={{ mt: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12}></Grid>
          <Grid item xs={12} sm={10}>
            <Box display="flex" alignItems="center" mb={2}>
              <Avatar
                sx={{
                  bgcolor: (theme) => theme.palette.primary.main,
                  width: 40,
                  height: 40,
                  mr: 1,
                }}
                onClick={() => setSelectedDate(moment())}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  width="24px"
                  height="24px"
                >
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" />
                </svg>
              </Avatar>

              <Box display="flex" alignItems="center">
                <IconArrowLeft
                  onClick={handlePrevDay}
                  style={{
                    cursor: "pointer",
                    marginLeft: 8,
                    borderRadius: "50%",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "#FFFFFF";
                    e.currentTarget.style.backgroundColor = "#0074BA";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "#000";
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                />
                <Typography
                  variant="h6"
                  component="div"
                  sx={{ fontWeight: "bold", mx: 2 }}
                >
                  {selectedDate.format("YYYY.MM.DD")}{" "}
                  <span
                    style={{
                      color:
                        selectedDate.day() === 0
                          ? "#d32f2f"
                          : selectedDate.day() === 6
                          ? "#1976d2"
                          : "inherit",
                    }}
                  >
                    ({getKoreanDayOfWeek(selectedDate)})
                  </span>
                </Typography>
                <IconArrowRight
                  onClick={handleNextDay}
                  style={{
                    cursor: "pointer",
                    marginLeft: 8,
                    borderRadius: "50%",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "#FFFFFF";
                    e.currentTarget.style.backgroundColor = "#0074BA";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "#000";
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                />
                <IconButton
                  onClick={handleCalendarClick}
                  style={{
                    cursor: "pointer",
                    marginLeft: 8,
                    borderRadius: "50%",
                  }}
                >
                  <IconCalendar />
                </IconButton>
                <Popover
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={handleCalendarClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "center",
                  }}
                  sx={{
                    "& .MuiPopover-paper": {
                      boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                      borderRadius: "12px",
                      p: 1,
                    },
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <StaticDatePicker
                      displayStaticWrapperAs="desktop"
                      openTo="day"
                      value={selectedDate}
                      onChange={(date: Moment | null) => {
                        if (date) {
                          handleCalendarDateSelect(date);
                        }
                      }}
                      renderInput={(
                        params: React.ComponentProps<typeof TextField>
                      ) => <TextField {...params} />}
                    />
                  </LocalizationProvider>
                </Popover>
              </Box>
            </Box>
            <TableContainer
              component={Paper}
              sx={{ height: 600, overflowY: "auto" }}
            >
              <Table sx={{ minWidth: 650, border: "1px solid #e0e0e0" }}>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        backgroundColor: "#f5f5f5",
                        fontWeight: "bold",
                        borderRight: "1px solid #e0e0e0",
                        textAlign: "center",
                        width: "10%",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      시간
                    </TableCell>
                    {doctorList.map((doctor) => (
                      <TableCell
                        key={doctor.id}
                        sx={{
                          backgroundColor: "#f5f5f5",
                          fontWeight: "bold",
                          borderRight: "1px solid #e0e0e0",
                          textAlign: "center",
                          width: `${90 / doctorList.length}%`,
                          position: "sticky",
                          top: 0,
                          zIndex: 1,
                        }}
                      >
                        <Box
                          display="flex"
                          flexDirection="column"
                          alignItems="center"
                        >
                          <img
                            src={doctor.profileImageUrl}
                            alt={doctor.name}
                            style={{
                              width: 50,
                              height: 50,
                              borderRadius: "50%",
                            }}
                          />
                          <Typography variant="body2">{doctor.name}</Typography>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {timeArray.map((time, i) => {
                    const isHalfHour = time.endsWith(":30");

                    return (
                      <TableRow
                        key={time}
                        ref={time === currentTime ? currentRowRef : null}
                        sx={{ height: "60px" }} // 모든 행의 높이를 60px로 설정
                      >
                        <TableCell
                          sx={{
                            fontWeight: "bold", // 모든 셀의 글꼴 굵기를 동일하게
                            fontSize: "0.875rem", // 모든 셀의 글꼴 크기를 동일하게
                            borderRight: "1px solid #e0e0e0",
                            textAlign: "center",
                            backgroundColor:
                              time === currentTime ? "#ffffe0" : "inherit",
                            width: "10%",
                            py: 1, // 모든 셀의 패딩을 동일하게
                          }}
                        >
                          {time}
                        </TableCell>
                        {doctorList.map((doctor, doctorIndex) => {
                          let cellColor = "#fff";

                          // 연차인 경우 보라색 배경으로 설정
                          const isOnTimeOff = isTimeOff(
                            doctor.id,
                            selectedDate,
                            time
                          );

                          // 점심 시간인지 확인 (API에서 받아온 값 기반)
                          const isOnLunchTime = isLunchTime(
                            doctor.id,
                            time,
                            selectedDate
                          );

                          if (isOnLunchTime) {
                            cellColor = "#ffe6e6"; // 점심시간 (연한 붉은색)
                          } else if (isOnTimeOff) {
                            cellColor = "#f3e5f5"; // 연한 보라색
                          } else if (time === currentTime) {
                            cellColor = "#ffffe0"; // 현재 시간 (연한 노란색)
                          }

                          // Check if this cell has a reservation
                          const hasReservation = reservation.some(
                            (res) =>
                              res.medicalStaff.id === doctor.id &&
                              moment(res.reservedAt).format("HH:mm") === time &&
                              res.status !== "DELETED"
                          );

                          if (
                            hasReservation &&
                            !isOnTimeOff &&
                            !isOnLunchTime
                          ) {
                            cellColor = "#e3f2fd"; // Light blue for reserved slots
                          }

                          // 의사 스케줄에 따라 가능 여부 확인
                          const isTimeAvailable =
                            !isOnTimeOff &&
                            !isOnLunchTime &&
                            isDoctorAvailable(doctor.id, time, selectedDate);

                          // 예약 불가능 시간은 회색 배경
                          if (
                            !isTimeAvailable &&
                            !isOnLunchTime &&
                            !isOnTimeOff
                          ) {
                            const adjacentType = checkAdjacentUnavailableTimes(
                              doctor.id,
                              time,
                              selectedDate
                            );
                            if (adjacentType === "middle") {
                              cellColor = "#f5f5f5"; // 중간 휴진 시간 (더 연한 회색)
                            } else {
                              cellColor = "#f5f5f5"; // 일반 휴진 시간 (회색)
                            }
                          }

                          const isAvailable = cellColor === "#fff";

                          return (
                            <TableCell
                              key={`${i}-${doctorIndex}`}
                              sx={{
                                border: "1px solid #ddd",
                                p: 1, // 모든 셀의 패딩을 동일하게
                                height: "60px", // 모든 셀의 높이를 60px로 설정
                                backgroundColor: cellColor,
                                cursor: isAvailable ? "pointer" : "default",
                                position: "relative",
                                "&:hover": {
                                  backgroundColor: isAvailable
                                    ? "#f5f5f5"
                                    : cellColor,
                                },
                                fontSize: "0.875rem", // 모든 셀의 글꼴 크기를 동일하게
                              }}
                              onClick={() => {
                                if (isAvailable && isTimeAvailable) {
                                  const timeText = time;
                                  const [hours, minutes] = timeText
                                    .split(":")
                                    .map(Number);
                                  const selectedDateTime = moment(selectedDate)
                                    .hours(hours)
                                    .minutes(minutes);
                                  handleOpenReservationModal(
                                    doctorList.find(
                                      (doc) => doc.id === doctor.id
                                    ),
                                    selectedDateTime
                                  );
                                }
                              }}
                            >
                              {isOnTimeOff ? (
                                <Box
                                  sx={{
                                    position: "absolute",
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "#7b1fa2",
                                      fontWeight: "bold",
                                      bgcolor: "#f3e5f5",
                                      px: 1.5, // 모든 표시의 패딩을 동일하게
                                      py: 0.7, // 모든 표시의 패딩을 동일하게
                                      borderRadius: "4px",
                                      border: "1px dashed #7b1fa2",
                                      display: "flex",
                                      alignItems: "center",
                                      fontSize: "0.75rem", // 모든 표시의 글꼴 크기를 동일하게
                                    }}
                                  >
                                    <Box
                                      component="span"
                                      sx={{
                                        fontSize: "16px", // 모든 아이콘 크기를 동일하게
                                        mr: 0.5,
                                      }}
                                    >
                                      🌴
                                    </Box>
                                    {getTimeOffMemo(
                                      doctor.id,
                                      selectedDate,
                                      time
                                    )}
                                  </Typography>
                                </Box>
                              ) : (
                                getReservationForTime(doctor.id, time)
                              )}
                              {/* Add a hint for empty slots */}
                              {isTimeAvailable &&
                                !isOnTimeOff &&
                                !isOnLunchTime &&
                                !hasReservation && (
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      opacity: "0",
                                      backgroundColor: "transparent",
                                      "&:hover": {
                                        opacity: "1",
                                        backgroundColor:
                                          "rgba(93, 135, 255, 0.1)",
                                      },
                                      zIndex: 1,
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const timeText = time;
                                      const [hours, minutes] = timeText
                                        .split(":")
                                        .map(Number);
                                      const selectedDateTime = moment(
                                        selectedDate
                                      )
                                        .hours(hours)
                                        .minutes(minutes);
                                      handleOpenReservationModal(
                                        doctorList.find(
                                          (doc) => doc.id === doctor.id
                                        ),
                                        selectedDateTime
                                      );
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        color: "primary.main",
                                        fontWeight: "medium",
                                        bgcolor: "primary.light",
                                        px: 1, // 모든 표시의 패딩을 동일하게
                                        py: 0.5, // 모든 표시의 패딩을 동일하게
                                        borderRadius: "4px",
                                        fontSize: "0.75rem", // 모든 표시의 글꼴 크기를 동일하게
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                      }}
                                    >
                                      {(() => {
                                        // 해당 시간에 이미 예약된 환자 수 확인
                                        const timeSlotReservations =
                                          reservation.filter(
                                            (res) =>
                                              res.medicalStaff.id ===
                                                doctor.id &&
                                              moment(res.reservedAt).format(
                                                "HH:mm"
                                              ) === time &&
                                              res.status !== "DELETED"
                                          );

                                        // 의사의 스케줄 정보를 가져옴
                                        const schedule =
                                          doctorSchedules[doctor.id];

                                        // 최대 예약 가능 환자 수 (API에서 받아온 값)
                                        const maxPatients =
                                          schedule?.availablePatientCount || 1;

                                        // 현재 예약 가능한 남은 환자 수
                                        const availableCount = Math.max(
                                          0,
                                          maxPatients -
                                            timeSlotReservations.length
                                        );

                                        return (
                                          <>
                                            <span>예약 가능</span>
                                            <span
                                              style={{
                                                fontSize: "0.7rem",
                                                marginTop: "2px",
                                              }}
                                            >
                                              {availableCount}명
                                            </span>
                                          </>
                                        );
                                      })()}
                                    </Typography>
                                  </Box>
                                )}

                              {!isTimeAvailable &&
                                !hasReservation &&
                                !isOnTimeOff && (
                                  <Box
                                    sx={{
                                      position: "absolute",
                                      top: 0,
                                      left: 0,
                                      right: 0,
                                      bottom: 0,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    {isOnLunchTime ? (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "#d32f2f",
                                          fontWeight: "bold",
                                          bgcolor: "#ffebee",
                                          px: 1.5, // 모든 표시의 패딩을 동일하게
                                          py: 0.7, // 모든 표시의 패딩을 동일하게
                                          borderRadius: "4px",
                                          border: "1px dashed #d32f2f",
                                          display: "flex",
                                          alignItems: "center",
                                          fontSize: "0.75rem", // 모든 표시의 글꼴 크기를 동일하게
                                        }}
                                      >
                                        <Box
                                          component="span"
                                          sx={{
                                            fontSize: "16px", // 모든 아이콘 크기를 동일하게
                                            mr: 0.5,
                                          }}
                                        >
                                          🍽️
                                        </Box>
                                        {getLunchTimeText(
                                          doctor.id,
                                          selectedDate
                                        )}
                                      </Typography>
                                    ) : (
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: "text.secondary",
                                          fontWeight: "medium",
                                          px: 1, // 모든 표시의 패딩을 동일하게
                                          py: 0.5, // 모든 표시의 패딩을 동일하게
                                          width: "100%",
                                          textAlign: "center",
                                          borderTop: (theme) => {
                                            const adjacentType =
                                              checkAdjacentUnavailableTimes(
                                                doctor.id,
                                                time,
                                                selectedDate
                                              );
                                            return adjacentType === "middle" ||
                                              adjacentType === "end"
                                              ? "none"
                                              : "0px solid #ddd";
                                          },
                                          borderBottom: (theme) => {
                                            const adjacentType =
                                              checkAdjacentUnavailableTimes(
                                                doctor.id,
                                                time,
                                                selectedDate
                                              );
                                            return adjacentType === "middle" ||
                                              adjacentType === "start"
                                              ? "none"
                                              : "";
                                          },
                                          borderRadius: (theme) => {
                                            const adjacentType =
                                              checkAdjacentUnavailableTimes(
                                                doctor.id,
                                                time,
                                                selectedDate
                                              );
                                            if (adjacentType === "isolated")
                                              return "4px";
                                            if (adjacentType === "start")
                                              return "4px 4px 0 0";
                                            if (adjacentType === "end")
                                              return "0 0 4px 4px";
                                            return "0";
                                          },
                                          fontSize: "0.75rem", // 모든 셀의 글꼴 크기를 동일하게
                                        }}
                                      >
                                        {checkAdjacentUnavailableTimes(
                                          doctor.id,
                                          time,
                                          selectedDate
                                        ) === "middle"
                                          ? ""
                                          : ""}
                                      </Typography>
                                    )}
                                  </Box>
                                )}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12} sm={2}>
            <Box display="flex" alignItems="center" mb={2}>
              <Avatar
                sx={{
                  bgcolor: (theme) => theme.palette.primary.light,
                  width: 40,
                  height: 40,
                  mr: 1,
                }}
              >
                <Avatar
                  src={icon1Img}
                  alt="icon"
                  sx={{
                    width: 20,
                    height: 20,
                  }}
                />
              </Avatar>
              <Typography
                variant="h6"
                component="div"
                sx={{ fontWeight: "bold" }}
              >
                환자 정보
              </Typography>
            </Box>
            {/* 업무카드 할일 */}
            <DashboardCard>
              <Box sx={{ height: 300, overflowY: "auto", px: 0.5 }}>
                {patient && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        mb: 1.5,
                      }}
                    >
                      <Avatar
                        sx={{
                          width: 40,
                          height: 40,
                          bgcolor:
                            patient?.patient?.gender === "MALE"
                              ? "#E3F2FD"
                              : "#FCE4EC",
                          color:
                            patient?.patient?.gender === "MALE"
                              ? "#1565C0"
                              : "#C2185B",
                          fontWeight: "bold",
                          fontSize: "1rem",
                          mr: 1,
                          boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                        }}
                      >
                        {patient?.patient?.name
                          ? patient.patient.name.charAt(0)
                          : ""}
                      </Avatar>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            fontWeight="600"
                            sx={{
                              fontSize: "0.85rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              mr: 0.5,
                            }}
                          >
                            {patient?.patient?.name || ""}
                          </Typography>
                          <Chip
                            size="small"
                            label={
                              patient?.status === "RESERVED"
                                ? "예약"
                                : patient?.status === "VISITED"
                                ? "방문"
                                : patient?.status === "NO_SHOW"
                                ? "미방문"
                                : "취소"
                            }
                            sx={{
                              height: 18,
                              fontSize: "0.6rem",
                              fontWeight: "bold",
                              bgcolor:
                                patient?.status === "RESERVED"
                                  ? "#E3F2FD"
                                  : patient?.status === "VISITED"
                                  ? "#E8F5E9"
                                  : patient?.status === "NO_SHOW"
                                  ? "#FFEBEE"
                                  : "#F5F5F5",
                              color:
                                patient?.status === "RESERVED"
                                  ? "#1565C0"
                                  : patient?.status === "VISITED"
                                  ? "#2E7D32"
                                  : patient?.status === "NO_SHOW"
                                  ? "#C62828"
                                  : "#616161",
                              flexShrink: 0,
                            }}
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            mt: 0.2,
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              display: "flex",
                              alignItems: "center",
                              mr: 1,
                              fontSize: "0.65rem",
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                width: 3,
                                height: 3,
                                borderRadius: "50%",
                                bgcolor:
                                  patient?.patient?.gender === "MALE"
                                    ? "#1565C0"
                                    : "#C2185B",
                                display: "inline-block",
                                mr: 0.3,
                              }}
                            />
                            {patient?.patient?.gender === "MALE" ? "남" : "여"}{" "}
                            {getAge(patient?.patient?.birthDate)}세
                          </Typography>
                          <Chip
                            label={
                              patient?.visitStatus === "FIRST" ? "초진" : "재진"
                            }
                            size="small"
                            sx={{
                              height: 14,
                              fontSize: "0.6rem",
                              fontWeight: "bold",
                              bgcolor:
                                patient?.visitStatus === "FIRST"
                                  ? "#FFF8E1"
                                  : "#E8F5E9",
                              color:
                                patient?.visitStatus === "FIRST"
                                  ? "#F57C00"
                                  : "#2E7D32",
                              flexShrink: 0,
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        mb: 1.5,
                        p: 1.2,
                        borderRadius: 1.5,
                        bgcolor: "grey.50",
                        border: "1px solid rgba(0,0,0,0.03)",
                      }}
                    >
                      <Grid container spacing={0.5}>
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              alignItems: "flex-start",
                              mb: 0.7,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                width: "60px",
                                fontSize: "0.65rem",
                                flexShrink: 0,
                                mt: 0.1,
                              }}
                            >
                              연락처
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              sx={{
                                fontSize: "0.75rem",
                                overflow: "hidden",
                                whiteSpace: "nowrap",
                                width: "calc(100%)",
                              }}
                            >
                              {patient?.patient?.mobile}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12}>
                          <Box
                            sx={{
                              alignItems: "flex-start",
                              mb: 0.7,
                            }}
                          >
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                width: "60px",
                                fontSize: "0.65rem",
                                flexShrink: 0,
                                mt: 0.1,
                              }}
                            >
                              예약 시간
                            </Typography>
                            <Typography
                              variant="body2"
                              fontWeight="medium"
                              sx={{
                                fontSize: "0.75rem",
                                color: "primary.main",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {patient?.reservedAt
                                ? moment(patient.reservedAt).format(
                                    "MM월 DD일 HH:mm"
                                  )
                                : ""}
                            </Typography>
                          </Box>
                        </Grid>

                        {patient?.patient?.lastVisitedAt && (
                          <Grid item xs={12}>
                            <Box
                              sx={{
                                alignItems: "flex-start",
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  width: "60px",
                                  fontSize: "0.65rem",
                                  flexShrink: 0,
                                  mt: 0.1,
                                }}
                              >
                                마지막 방문
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: "0.75rem",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {moment(patient?.patient?.lastVisitedAt).format(
                                  "YYYY.MM.DD"
                                )}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Box>

                    {patient?.memo && (
                      <Box
                        sx={{
                          mb: 1.5,
                          p: 1.2,
                          borderRadius: 1.5,
                          borderLeft: "3px solid #FFC107",
                          bgcolor: "#FFF8E1",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: "block",
                            mb: 0.3,
                            fontSize: "0.65rem",
                          }}
                        >
                          불편사항
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: "0.75rem",
                            lineHeight: 1.3,
                            wordBreak: "break-word",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                          }}
                        >
                          {patient?.memo}
                        </Typography>
                      </Box>
                    )}

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mt: 1.5,
                        pt: 1.2,
                        borderTop: "1px solid rgba(0,0,0,0.06)",
                      }}
                    >
                      {patient?.visitedAt ? (
                        <Button
                          variant="contained"
                          disabled
                          startIcon={<IconCheck size={12} />}
                          size="small"
                          sx={{
                            bgcolor: "#4CAF50",
                            "&.Mui-disabled": {
                              bgcolor: "#E8F5E9",
                              color: "#2E7D32",
                            },
                            textTransform: "none",
                            fontWeight: "medium",
                            boxShadow: "none",
                            borderRadius: 1.5,
                            fontSize: "0.65rem",
                            py: 0.4,
                            px: 1,
                            minWidth: "85px",
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          방문{" "}
                          {patient?.visitedAt
                            ? moment(patient.visitedAt).format("HH:mm")
                            : ""}
                        </Button>
                      ) : patient?.deletedAt == null ? (
                        <Button
                          variant="contained"
                          onClick={() =>
                            patient?.id ? reservationVisit(patient.id) : null
                          }
                          size="small"
                          sx={{
                            bgcolor: "#FF9800",
                            "&:hover": {
                              bgcolor: "#F57C00",
                            },
                            textTransform: "none",
                            fontWeight: "bold",
                            boxShadow: "0 2px 4px rgba(255,152,0,0.2)",
                            borderRadius: 1.5,
                            fontSize: "0.65rem",
                            py: 0.4,
                            px: 1,
                            minWidth: "85px",
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          방문처리
                        </Button>
                      ) : (
                        <Button
                          disabled
                          size="small"
                          sx={{ py: 0.4, px: 1, fontSize: "0.65rem" }}
                        >
                          -
                        </Button>
                      )}
                      <Button
                        variant="outlined"
                        color="error"
                        onClick={() =>
                          patient?.id ? reservationDelete(patient.id) : null
                        }
                        size="small"
                        sx={{
                          textTransform: "none",
                          fontWeight: "medium",
                          borderColor: "#EF5350",
                          color: "#EF5350",
                          borderRadius: 1.5,
                          "&:hover": {
                            borderColor: "#D32F2F",
                            bgcolor: "rgba(211, 47, 47, 0.04)",
                            boxShadow: "0 2px 4px rgba(211,47,47,0.1)",
                          },
                          fontSize: "0.65rem",
                          py: 0.4,
                          px: 1,
                          minWidth: "60px",
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        삭제
                      </Button>
                    </Box>
                  </>
                )}

                {!patient && (
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexDirection: "column",
                      p: 2,
                      textAlign: "center",
                      color: "text.secondary",
                    }}
                  >
                    <Box
                      sx={{
                        width: 70,
                        height: 70,
                        borderRadius: "50%",
                        bgcolor: "grey.50",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                        boxShadow: "inset 0 0 8px rgba(0,0,0,0.03)",
                      }}
                    >
                      <IconUserPlus size={30} color="#9E9E9E" />
                    </Box>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1, fontSize: "0.8rem" }}
                    >
                      환자를 선택하면 상세 정보가 표시됩니다
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "primary.main",
                        bgcolor: "primary.lighter",
                        py: 0.5,
                        px: 1,
                        borderRadius: 5,
                        fontSize: "0.7rem",
                      }}
                    >
                      예약 목록에서 환자를 클릭하세요
                    </Typography>
                  </Box>
                )}
              </Box>
            </DashboardCard>
            <div className="mt-5"></div>
            <DashboardCard>
              <>
                <Avatar
                  variant="rounded"
                  sx={{
                    bgcolor: (theme) => theme.palette.success.light,
                    width: 60,
                    height: 60,
                  }}
                >
                  <Avatar
                    src={icon1Img}
                    alt="icon"
                    sx={{ width: 24, height: 24 }}
                  />
                </Avatar>

                <Typography variant="subtitle2" color="textSecondary" mt={3}>
                  오늘 예약환자
                </Typography>
                <Typography variant="h4" mt={1}>
                  {`${
                    reservation.filter((arr) => arr.status !== "DELETED").length
                  }명`}
                </Typography>
                <Stack direction="row" spacing={1} mt={1} alignItems="center">
                  <Avatar sx={{ width: 20, height: 20 }}>
                    <IconCheck width={16} color="#39B69A" />
                  </Avatar>
                  <Typography variant="subtitle2" color="textSecondary">
                    방문건수:
                    {reservation.filter((arr) => arr.visitedAt).length} 명
                  </Typography>
                </Stack>
              </>
            </DashboardCard>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={6} lg={4}>
          <ChatWeekly />
        </Grid>

        {/* Reservation Modal */}
        <Dialog
          open={openReservationModal}
          onClose={handleCloseReservationModal}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: "12px",
              boxShadow: "0 10px 40px rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            sx={{
              bgcolor: "primary.main",
              color: "white",
              py: 2,
              px: 3,
              fontSize: "1.3rem",
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box display="flex" alignItems="center">
              <IconCalendar style={{ marginRight: 12 }} />
              환자 예약 등록
            </Box>
            <IconButton
              size="small"
              onClick={handleCloseReservationModal}
              sx={{ color: "white" }}
            >
              <IconX />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ py: 3, px: 3 }}>
            <Grid container spacing={3}>
              {/* Doctor Selection */}
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    p: 2,
                    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.05)",
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color="primary.main"
                    mb={2}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Box
                      sx={{
                        bgcolor: "primary.light",
                        color: "primary.main",
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 1,
                      }}
                    >
                      1
                    </Box>
                    의사 선택
                  </Typography>
                  <FormControl fullWidth>
                    <InputLabel id="doctor-select-label">의사 선택</InputLabel>
                    <Select
                      labelId="doctor-select-label"
                      value={selectedDoctor?.id || ""}
                      label="의사 선택"
                      onChange={(e) => {
                        const doctor = doctorList.find(
                          (doc) => doc.id === e.target.value
                        );
                        setSelectedDoctor(doctor);
                      }}
                      sx={{
                        "& .MuiSelect-select": {
                          display: "flex",
                          alignItems: "center",
                        },
                      }}
                    >
                      {doctorList.map((doctor) => (
                        <MenuItem
                          key={doctor.id}
                          value={doctor.id}
                          sx={{ display: "flex", alignItems: "center" }}
                        >
                          <Avatar
                            src={doctor.profileImageUrl}
                            alt={doctor.name}
                            sx={{ width: 36, height: 36, mr: 2 }}
                          />
                          <Typography>{doctor.name} 의사</Typography>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {selectedDoctor && (
                    <Box mt={2} p={2} bgcolor="grey.50" borderRadius={2}>
                      <Box display="flex" alignItems="center">
                        <Avatar
                          src={selectedDoctor.profileImageUrl}
                          alt={selectedDoctor.name}
                          sx={{
                            width: 64,
                            height: 64,
                            mr: 2,
                            boxShadow: "0 3px 10px rgba(0,0,0,0.1)",
                          }}
                        />
                        <Box>
                          <Typography variant="h6" fontWeight="bold">
                            {selectedDoctor.name} 의사
                          </Typography>
                          <Chip
                            label="내과 전문의"
                            size="small"
                            sx={{
                              bgcolor: "primary.light",
                              color: "primary.main",
                              fontWeight: "medium",
                              mt: 0.5,
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Card>
              </Grid>

              {/* Date and Time Selection */}
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    p: 2,
                    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.05)",
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color="primary.main"
                    mb={2}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Box
                      sx={{
                        bgcolor: "primary.light",
                        color: "primary.main",
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 1,
                      }}
                    >
                      2
                    </Box>
                    예약 시간
                  </Typography>

                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <DatePicker
                          label="예약 날짜"
                          value={selectedTime}
                          onChange={(newValue: Moment | null) => {
                            if (newValue && selectedTime) {
                              const newDateTime = moment(newValue);
                              newDateTime.hours(selectedTime.hours());
                              newDateTime.minutes(selectedTime.minutes());
                              setSelectedTime(newDateTime);
                            } else {
                              setSelectedTime(newValue || moment());
                            }
                          }}
                          renderInput={(
                            params: React.ComponentProps<typeof TextField>
                          ) => (
                            <TextField {...params} fullWidth sx={{ mb: 2 }} />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TimePicker
                          label="예약 시간"
                          value={selectedTime}
                          onChange={(newValue: Moment | null) => {
                            setSelectedTime(newValue || moment());
                          }}
                          renderInput={(
                            params: React.ComponentProps<typeof TextField>
                          ) => <TextField {...params} fullWidth />}
                        />
                      </Grid>
                    </Grid>
                  </LocalizationProvider>

                  {selectedTime && (
                    <Box
                      mt={2}
                      p={2}
                      bgcolor="grey.50"
                      borderRadius={2}
                      display="flex"
                      alignItems="center"
                    >
                      <Avatar
                        sx={{
                          bgcolor: "primary.light",
                          color: "primary.main",
                          width: 48,
                          height: 48,
                          mr: 2,
                        }}
                      >
                        <IconAlarm />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          예약 일시
                        </Typography>
                        <Typography variant="h6" fontWeight="bold">
                          {selectedTime.format("YYYY년 MM월 DD일 HH:mm")}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Card>
              </Grid>

              {/* Patient Selection */}
              <Grid item xs={12}>
                <Card
                  sx={{ p: 2, boxShadow: "0 2px 12px rgba(0, 0, 0, 0.05)" }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color="primary.main"
                    mb={2}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Box
                      sx={{
                        bgcolor: "primary.light",
                        color: "primary.main",
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 1,
                      }}
                    >
                      3
                    </Box>
                    환자 선택
                  </Typography>

                  <TextField
                    label="환자 검색"
                    variant="outlined"
                    fullWidth
                    value={searchQuery}
                    onChange={(e) => handleSearchQueryChange(e, e.target.value)}
                    placeholder="환자 이름으로 검색"
                    InputProps={{
                      endAdornment: isLoading ? (
                        <CircularProgress size={20} />
                      ) : null,
                    }}
                    sx={{ mb: 2 }}
                  />

                  <Box
                    sx={{
                      height: 250,
                      overflowY: "auto",
                      mb: 2,
                      border: "1px solid #e0e0e0",
                      borderRadius: 1,
                    }}
                    onScroll={handlePatientListScroll}
                  >
                    {patients.length > 0 ? (
                      patients.map((patient) => (
                        <Box
                          key={patient.id}
                          onClick={() => setSelectedPatient(patient)}
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            p: 1.5,
                            borderBottom: "1px solid #f0f0f0",
                            cursor: "pointer",
                            transition: "background-color 0.2s",
                            backgroundColor:
                              selectedPatient?.id === patient.id
                                ? "rgba(25, 118, 210, 0.08)"
                                : "transparent",
                            "&:hover": {
                              backgroundColor: "rgba(0, 0, 0, 0.04)",
                            },
                            borderLeft: `4px solid ${
                              patient.gender === "MALE" ? "#1976d2" : "#d81b60"
                            }`,
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor:
                                patient.gender === "MALE"
                                  ? "#e3f2fd"
                                  : "#fce4ec",
                              color:
                                patient.gender === "MALE"
                                  ? "#0d47a1"
                                  : "#880e4f",
                              width: 45,
                              height: 45,
                              mr: 2,
                              fontSize: "1.2rem",
                              fontWeight: "bold",
                            }}
                          >
                            {patient.name && patient.name.length > 0
                              ? patient.name[0]
                              : ""}
                          </Avatar>
                          <Box sx={{ width: "100%" }}>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                mb: 0.5,
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight:
                                    selectedPatient?.id === patient.id
                                      ? "bold"
                                      : "medium",
                                  fontSize: "0.95rem",
                                }}
                              >
                                {patient.name}
                              </Typography>
                              <Chip
                                size="small"
                                label={
                                  patient.gender === "MALE" ? "남성" : "여성"
                                }
                                sx={{
                                  height: 20,
                                  fontSize: "0.65rem",
                                  bgcolor:
                                    patient.gender === "MALE"
                                      ? "#e3f2fd"
                                      : "#fce4ec",
                                  color:
                                    patient.gender === "MALE"
                                      ? "#1565c0"
                                      : "#c2185b",
                                  fontWeight: "600",
                                }}
                              />
                            </Box>

                            <Box
                              sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "flex",
                                  alignItems: "center",
                                  fontSize: "0.75rem",
                                }}
                              >
                                <Box
                                  component="span"
                                  sx={{ mr: 0.5, fontWeight: 500 }}
                                >
                                  나이:
                                </Box>
                                {getAge(patient.birthDate)}세
                              </Typography>

                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  display: "flex",
                                  alignItems: "center",
                                  fontSize: "0.75rem",
                                }}
                              >
                                <Box
                                  component="span"
                                  sx={{ mr: 0.5, fontWeight: 500 }}
                                >
                                  생년월일:
                                </Box>
                                {moment(patient.birthDate).format("YYYY-MM-DD")}
                              </Typography>
                            </Box>

                            <Box
                              sx={{
                                display: "flex",
                                mt: 0.5,
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  color: "text.secondary",
                                  fontSize: "0.75rem",
                                }}
                              >
                                <Box
                                  component="span"
                                  sx={{ mr: 0.5, fontWeight: 500 }}
                                >
                                  연락처:
                                </Box>
                                {patient.mobile}
                              </Typography>

                              {patient.lastVisitedAt && (
                                <Typography
                                  variant="caption"
                                  sx={{
                                    color: "success.main",
                                    fontSize: "0.7rem",
                                    bgcolor: "success.light",
                                    py: 0.3,
                                    px: 0.8,
                                    borderRadius: 5,
                                    fontWeight: 500,
                                  }}
                                >
                                  최근방문:{" "}
                                  {moment(patient.lastVisitedAt).format(
                                    "YYYY-MM-DD"
                                  )}
                                </Typography>
                              )}

                              {!patient.lastVisitedAt && (
                                <Chip
                                  size="small"
                                  label="초진"
                                  sx={{
                                    height: 18,
                                    fontSize: "0.65rem",
                                    bgcolor: "#fff8e1",
                                    color: "#e65100",
                                    fontWeight: "600",
                                  }}
                                />
                              )}
                            </Box>
                          </Box>
                        </Box>
                      ))
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          p: 3,
                        }}
                      >
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {isLoading
                            ? "환자 목록을 불러오는 중..."
                            : "검색 결과가 없습니다"}
                        </Typography>
                        {!isLoading && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={handleOpenPatientModal}
                            startIcon={<Add />}
                          >
                            새 환자 등록
                          </Button>
                        )}
                      </Box>
                    )}
                    {isLoading && patients.length > 0 && (
                      <Box
                        sx={{ display: "flex", justifyContent: "center", p: 1 }}
                      >
                        <CircularProgress size={24} />
                      </Box>
                    )}
                  </Box>

                  {selectedPatient && (
                    <Box mt={2} p={2} bgcolor="grey.50" borderRadius={2}>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        mb={1}
                      >
                        선택된 환자:
                      </Typography>
                      <Box display="flex" alignItems="center">
                        <Avatar
                          sx={{
                            bgcolor:
                              selectedPatient.gender === "MALE"
                                ? "#e3f2fd"
                                : "#fce4ec",
                            color:
                              selectedPatient.gender === "MALE"
                                ? "#0d47a1"
                                : "#880e4f",
                            width: 56,
                            height: 56,
                            mr: 2,
                          }}
                        >
                          {selectedPatient.name &&
                          selectedPatient.name.length > 0
                            ? selectedPatient.name[0]
                            : ""}
                        </Avatar>
                        <Box>
                          <Typography variant="h6" fontWeight="bold">
                            {selectedPatient.name}
                          </Typography>
                          <Typography variant="body2">
                            {getAge(selectedPatient.birthDate)}세 •
                            {selectedPatient.gender === "MALE"
                              ? " 남성"
                              : " 여성"}{" "}
                            •{selectedPatient.mobile}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Card>
              </Grid>

              {/* Visit Type and Memo */}
              <Grid item xs={12}>
                <Card
                  sx={{ p: 2, boxShadow: "0 2px 12px rgba(0, 0, 0, 0.05)" }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color="primary.main"
                    mb={2}
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <Box
                      sx={{
                        bgcolor: "primary.light",
                        color: "primary.main",
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mr: 1,
                      }}
                    >
                      4
                    </Box>
                    진료 정보
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel id="visit-type-label">진료 유형</InputLabel>
                        <Select
                          labelId="visit-type-label"
                          value={
                            selectedPatient?.lastVisitedAt
                              ? "FOLLOW_UP"
                              : "FIRST"
                          }
                          label="진료 유형"
                          disabled
                        >
                          <MenuItem value="FIRST">
                            <Chip
                              label="초진"
                              size="small"
                              sx={{
                                bgcolor: "#fff8e1",
                                color: "#e65100",
                                fontWeight: "bold",
                              }}
                            />
                          </MenuItem>
                          <MenuItem value="FOLLOW_UP">
                            <Chip
                              label="재진"
                              size="small"
                              sx={{
                                bgcolor: "#e3fcef",
                                color: "#00695c",
                                fontWeight: "bold",
                              }}
                            />
                          </MenuItem>
                        </Select>
                        <FormHelperText>
                          환자의 방문 이력에 따라 자동으로 설정됩니다
                        </FormHelperText>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Box
                        display="flex"
                        alignItems="center"
                        height="100%"
                        p={1}
                      >
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          mr={2}
                        >
                          {selectedPatient?.lastVisitedAt
                            ? "✅ 재진 환자는 이전 방문 기록이 있습니다."
                            : "❗ 첫 방문 환자는 진료 전 병원 접수 시스템에 등록이 필요합니다."}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>

                  <Box mt={2}>
                    <TextField
                      label="메모"
                      multiline
                      rows={3}
                      fullWidth
                      value={reservationMemo}
                      onChange={(e) => setReservationMemo(e.target.value)}
                      placeholder="환자의 특이사항 또는 불편사항을 메모하세요"
                      InputProps={{
                        startAdornment: (
                          <IconNote
                            style={{ color: "#757575", marginRight: 8 }}
                          />
                        ),
                      }}
                    />
                  </Box>
                </Card>
              </Grid>
            </Grid>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2, bgcolor: "grey.50" }}>
            <Button
              onClick={handleCloseReservationModal}
              sx={{
                color: "text.secondary",
                fontWeight: "bold",
                px: 3,
              }}
            >
              취소
            </Button>
            <Button
              variant="contained"
              onClick={handleReservationSubmit}
              disabled={
                !selectedDoctor ||
                !selectedTime ||
                !selectedPatient ||
                submitting
              }
              startIcon={submitting ? null : <IconDeviceFloppy />}
              sx={{
                bgcolor: "primary.main",
                px: 4,
                py: 1,
                fontWeight: "bold",
                boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                "&:hover": {
                  bgcolor: "primary.dark",
                },
                "&.Mui-disabled": {
                  bgcolor: "action.disabledBackground",
                },
              }}
            >
              {submitting ? "저장 중..." : "예약 등록"}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      {/* 환자 등록 모달 */}
      <PatientModal
        open={openPatientModal}
        onClose={handleClosePatientModal}
        onSubmit={handlePatientSubmit}
        patientData={patientData}
        handleInputChange={handlePatientInputChange}
        handleSelectChange={handlePatientSelectChange}
        handleQuickBirthDate={handleQuickBirthDate}
      />
    </PageContainer>
  );
};

export default MainTwo;
